import { VaTextInput } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect } from 'react';

import { TRANSCRIPT_NAVIGATION_INSTRUCTIONS } from '../../utils/announcementText';

export const DEFAULT_CHARACTER_LIMIT = 400;
const LIMIT_ANNOUNCEMENT_DELAY_MS = 1500;
const COMPOSER_LABEL = 'Type your message';

const getCharCountText = (value, limit) => {
  const remaining = Math.max(limit - value.length, 0);
  const word = remaining === 1 ? 'character' : 'characters';
  const suffix = value.length === 0 ? 'allowed' : 'left';
  return `${remaining} ${word} ${suffix}`;
};

/**
 * @typedef {Object} ChatComposerProps
 * @property {function(string): void} onSubmit
 * @property {number} [characterLimit]
 * @property {boolean} [disabled]
 */

/**
 * Chat input composer for writing and submitting user messages.
 * @component
 * @param {ChatComposerProps} props
 * @returns {JSX.Element}
 */
export default function ChatComposer({
  onComposerInputReady,
  onTranscriptNavigationRequest,
  onSubmit,
  characterLimit = DEFAULT_CHARACTER_LIMIT,
  disabled = false,
  srStatusMessage = '',
}) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [limitAnnouncement, setLimitAnnouncement] = useState('');
  const [heldStatusMessage, setHeldStatusMessage] = useState('');
  // Read on entering the field. Recomputed only while unfocused: a describedby
  // that changed per keystroke makes JAWS re-read the description mid-typing.
  const [entryCountText, setEntryCountText] = useState(
    getCharCountText('', characterLimit),
  );
  const inputRef = useRef();
  const limitAnnouncementCountRef = useRef(0);

  if (srStatusMessage && srStatusMessage !== heldStatusMessage) {
    setHeldStatusMessage(srStatusMessage);
  }

  const onInputHandler = event => {
    const value = event.target.value || '';
    setInputValue(value);

    if (error && value.trim().length > 0) {
      setError('');
    }
  };

  const handleSubmit = event => {
    event.preventDefault();

    const message = inputValue.trim();
    if (disabled) {
      return;
    }

    if (!message || message.length === 0) {
      setError('This field is required');
      return;
    }

    onSubmit?.(message);
    setInputValue('');

    inputRef.current.value = '';
    setError('');
    setLimitAnnouncement('');
    setEntryCountText(getCharCountText('', characterLimit));
  };

  const onKeyDownHandler = event => {
    // for moving up out of the input and into the last message via keyboard
    if (
      event.key === 'ArrowUp' &&
      typeof onTranscriptNavigationRequest === 'function' &&
      (event.target?.value || '').trim().length === 0
    ) {
      event.preventDefault();
      onTranscriptNavigationRequest();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  // Allow time for VoiceOver character and word echo before announcing the limit.
  // Cancel on deletion, submission, or unmount to avoid stale announcements.
  useEffect(() => {
    if (inputValue.length < characterLimit) {
      setLimitAnnouncement('');
      return undefined;
    }

    const timer = setTimeout(() => {
      // VoiceOver can suppress identical text even after the live region clears.
      // Alternate equivalent messages, including across submissions. Only advance
      // when the timer fires so cancelled announcements do not consume a turn.
      const text =
        limitAnnouncementCountRef.current % 2 === 0
          ? getCharCountText(inputValue, characterLimit)
          : 'Character limit reached';
      limitAnnouncementCountRef.current += 1;
      setLimitAnnouncement(text);
    }, LIMIT_ANNOUNCEMENT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [inputValue, characterLimit]);

  // Expose the composer input to the parent so transcript nav
  // and errors can move focus back to the composer as needed
  useEffect(() => {
    if (typeof onComposerInputReady === 'function') {
      onComposerInputReady(inputRef.current);
    }
  }, [onComposerInputReady]);

  return (
    <form
      className="vads-u-padding-x--0p5 vads-grid-row vads-u-margin-bottom--1 va-chatbot-composer"
      onSubmit={handleSubmit}
    >
      <div className="vads-grid-col-11">
        {error && (
          <p
            className="vads-u-margin-bottom--0 vads-u-font-weight--bold vads-u-color--secondary-dark vads-u-padding-left--0p5 vads-u-margin-top--0p5"
            data-testid="chat-composer-error"
          >
            {error}
          </p>
        )}
        <VaTextInput
          data-testid="chat-composer-input"
          className="va-chatbot-composer__input"
          label={
            heldStatusMessage
              ? `${heldStatusMessage}. ${COMPOSER_LABEL}`
              : COMPOSER_LABEL
          }
          maxlength={characterLimit.toString()}
          message-aria-describedby={`${entryCountText}. ${TRANSCRIPT_NAVIGATION_INSTRUCTIONS}`}
          name="chat-composer-input"
          onBlur={() => {
            setEntryCountText(getCharCountText(inputValue, characterLimit));
            if (!srStatusMessage) {
              setHeldStatusMessage('');
            }
          }}
          onInput={onInputHandler}
          onKeyDown={onKeyDownHandler}
          ref={inputRef}
          value={inputValue}
          type="text"
          disabled={disabled || undefined}
          error={error || undefined}
          show-input-error={false}
        >
          {inputValue.length === 0 && (
            <span
              aria-hidden="true"
              className="va-chatbot-composer__placeholder"
              data-testid="chat-composer-placeholder"
            >
              {COMPOSER_LABEL}
            </span>
          )}
        </VaTextInput>
        <span
          aria-hidden="true"
          className="va-chatbot-composer__char-count"
          data-testid="chat-composer-char-count"
        >
          {getCharCountText(inputValue, characterLimit)}
        </span>
        {/* Not in aria-describedby, so JAWS does not read it twice */}
        <span
          aria-atomic="false"
          aria-live="polite"
          aria-relevant="additions"
          role="log"
          className="sr-only"
          data-testid="chat-composer-char-count-sr"
        >
          {limitAnnouncement && <span>{limitAnnouncement}</span>}
        </span>
      </div>

      <div className="vads-grid-col-1 vads-u-padding-left--0p5 vads-u-padding-top--0p5 vads-u-margin-top--0p25">
        {/* Native button is intentional: va-button only supports text labels
            and cannot render this icon-only control. */}
        {/* eslint-disable-next-line @department-of-veterans-affairs/prefer-button-component */}
        <button
          aria-label="Send your message to VA chatbot"
          className="vads-u-margin-x--0 vads-u-padding-x--0p25 vads-u-margin-bottom--3 usa-button-secondary usa-button--outline va-chatbot-composer__submit vads-u-border--0"
          data-testid="chat-composer-submit"
          disabled={disabled}
          type="submit"
        >
          <va-icon icon="send" size={3} />
        </button>
      </div>
    </form>
  );
}

ChatComposer.propTypes = {
  characterLimit: PropTypes.number,
  disabled: PropTypes.bool,
  srStatusMessage: PropTypes.string,
  onComposerInputReady: PropTypes.func,
  onSubmit: PropTypes.func,
  onTranscriptNavigationRequest: PropTypes.func,
};
